import { useEffect, useState } from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import { AppLayout } from '@/components/layout/AppLayout'
import { RichTextEditor } from '@/components/editor/RichTextEditor'
import { Input } from '@/components/ui/Input'
import { TagInput } from '@/components/ui/TagInput'
import { fetchThemes } from '@/api/themes'
import { useResource } from '@/hooks/useResource'
import { fetchReading, updateReading } from '@/api/readings'
import { DIFFICULTY_LEVELS, DIFFICULTY_LABELS } from '@/types/reading'
import type { Reading, DifficultyLevel } from '@/types/reading'
import type { SharedProps } from '@/types'
import { PageContainer } from '@/components/layout/PageContainer'
import { useCurrentLanguage } from '@/hooks/useCurrentLanguage'

interface Props {
  id: number
}

export default function ReadingsEdit() {
  const { id } = usePage().props as unknown as SharedProps & Props

  const [reading, setReading] = useState<Reading | null>(null)
  useCurrentLanguage(reading?.language_id)
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('a1')
  const [themeNames, setThemeNames] = useState<string[]>([])
  const { data: themeSuggestions } = useResource(() => fetchThemes().then((res) => res.data.data), [])
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchReading(id)
      .then((res) => {
        const r = res.data.data
        setReading(r)
        setTitle(r.title)
        setBody(r.body ?? '')
        setDifficulty(r.difficulty_level)
        setThemeNames(r.themes)
      })
      .finally(() => setLoading(false))
  }, [id])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setErrors({})

    updateReading(id, { title, body, difficulty_level: difficulty, theme_names: themeNames })
      .then(() => {
        router.visit(`/readings/${id}`)
      })
      .catch((err: { response?: { data?: { errors?: Record<string, string[]> } } }) => {
        if (err.response?.data?.errors) {
          setErrors(err.response.data.errors)
        }
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <AppLayout>
      <PageContainer width="prose" className="py-12 sm:py-16">
        {loading && <p className="text-muted text-sm">Loading…</p>}

        {!loading && reading && (
          <>
            <Link
              href={`/readings/${id}`}
              className="text-sm text-muted hover:text-body transition-colors duration-200 font-light"
            >
              &larr; Back to reading
            </Link>

            <h1 className="font-serif text-3xl font-medium text-heading mt-6 mb-10">
              Edit Reading
            </h1>

            <form onSubmit={handleSubmit} className="space-y-8">
              <Input
                label="Title"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                error={errors.title?.[0]}
                required
              />

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="difficulty"
                  className="text-xs uppercase tracking-widest text-label font-light"
                >
                  Difficulty Level
                </label>
                <select
                  id="difficulty"
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as DifficultyLevel)}
                  className="w-full rounded-md border border-border-divider bg-surface-bg px-4 py-3 text-sm text-body font-light focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30 transition-colors duration-200"
                >
                  {DIFFICULTY_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {DIFFICULTY_LABELS[level]}
                    </option>
                  ))}
                </select>
                {errors.difficulty_level?.[0] && (
                  <p role="alert" className="text-xs text-error font-light">
                    {errors.difficulty_level[0]}
                  </p>
                )}
              </div>

              <TagInput
                id="themes"
                label="Themes"
                value={themeNames}
                onChange={setThemeNames}
                suggestions={themeSuggestions ?? []}
                maxLength={40}
              />

              <div className="flex flex-col gap-1.5">
                <label className="text-xs uppercase tracking-widest text-label font-light">
                  Content
                </label>
                <RichTextEditor content={body} onChange={setBody} />
                {errors.body?.[0] && (
                  <p role="alert" className="text-xs text-error font-light">
                    {errors.body[0]}
                  </p>
                )}
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-block rounded-full px-8 py-3.5 text-sm font-light tracking-wide transition-colors duration-300 bg-btn-filled-bg text-btn-filled-text hover:bg-btn-filled-hover-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:opacity-50"
                >
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </>
        )}
      </PageContainer>
    </AppLayout>
  )
}
